/**
 * ข้อมูลจำลองสำหรับกลุ่มหน้าจอที่ 3 — การตั้งค่าโปรแกรมสะสมคะแนน
 * (FR-ADM-20, 21, 22, 23, 24 และ SRS ข้อ 3.4)
 *
 * แหล่งข้อมูลจำลองแห่งเดียวของกลุ่มนี้ ไม่เก็บลง localStorage
 * (localStorage ใช้เฉพาะการจำค่าที่ผู้ใช้ตั้งเอง ผ่าน usePersistentState)
 *
 * ⚠ SRS ข้อ 3.4 เขียนไว้ตรง ๆ ว่า "ตัวเลขที่ปรากฏในเอกสารนี้เป็นค่าตั้งต้นเพื่อการทดสอบเท่านั้น"
 *   ทุกค่าในไฟล์นี้จึงมี provisional: true ยกเว้นค่าที่ยืนยันแล้วในที่ประชุม
 *   หน้าจอทุกหน้าต้องแสดงป้าย "ค่าตั้งต้น" ที่แถวนั้น ไม่ใช่แค่หมายเหตุท้ายเอกสาร
 *
 * ค่าที่รอคำตอบ (ดู คำถามที่ยังค้างกับทีม backend, คำถามที่ยังค้างกับทีม backend ในภาคผนวก SRS และ PROGRESS-REPORT 8.x):
 *   — อายุคะแนน: SRS FR-PT-13 บอกว่า "รองรับการกำหนด" แต่ไม่บอกว่าจะตั้งเท่าไร
 *   — รอบรีเซ็ตยอดสะสม: BR-03 บอก "รอบปีปฏิทิน" แต่ คำถามที่ยังค้างกับทีม backend ยังไม่ตอบว่ายอดคงค้างทำอย่างไร
 *   — เงื่อนไขใช้ส่วนลดร่วมกัน (คำถามที่ยังค้างกับทีม backend) — ยังไม่ตอบ ค่าในไฟล์นี้ตั้งเป็น "ห้ามใช้ร่วม" ไว้ก่อน
 *   — อายุรหัสหลังกดรับสิทธิ์ 15 นาที มาจาก mockup แอป ไม่ได้อยู่ใน SRS
 */

/* ==========================================================================
   1. กติกาคะแนน (FR-ADM-24)
   ========================================================================== */

/** รอบการรีเซ็ตยอดซื้อสะสมที่ใช้คำนวณ Tier */
export type ResetCycle = 'calendar' | 'fiscal' | 'signup';

export const RESET_CYCLE_LABEL: Record<ResetCycle, string> = {
  calendar: 'ปีปฏิทิน (1 มกราคม)',
  fiscal: 'ปีงบประมาณ (เลือกเดือนเริ่ม)',
  signup: 'นับจากวันที่สมัครของแต่ละคน',
};

export const RESET_CYCLE_NOTE: Record<ResetCycle, string> = {
  calendar: 'ทุกคนรีเซ็ตวันเดียวกัน — งานฝ่ายบัญชีและการแจ้งเตือนกระจุกอยู่ต้นปี',
  fiscal: 'ตรงกับรอบบัญชีขององค์กร แต่ลูกค้าทั่วไปจะไม่เข้าใจว่าทำไมรีเซ็ตกลางปี',
  signup: 'ยุติธรรมกับคนที่สมัครปลายปี แต่ระบบต้องคำนวณรอบของแต่ละคนแยกกัน และรายงานรายปีจะเทียบกันตรง ๆ ไม่ได้',
};

/** สิ่งที่เกิดกับยอดสะสมและ Tier เมื่อถึงวันรีเซ็ต — คำถามที่ยังค้างกับทีม backend ยังไม่ตอบ */
export type ResetPolicy = 'drop-immediately' | 'grace-period' | 'keep-tier-one-year';

export const RESET_POLICY_LABEL: Record<ResetPolicy, string> = {
  'drop-immediately': 'ยอดสะสมเป็นศูนย์ และปรับระดับลงทันที',
  'grace-period': 'ยอดเป็นศูนย์ แต่คงระดับเดิมไว้ช่วงผ่อนผัน',
  'keep-tier-one-year': 'ยอดเป็นศูนย์ และคงระดับเดิมไว้อีก 1 ปีเต็ม',
};

export interface PointRule {
  /** ซื้อครบกี่บาท (ฐานของอัตราแลกเปลี่ยน) */
  spendPerPoint: number;
  /** ได้กี่คะแนน */
  pointsEarned: number;
  /** ปัดเศษยอดที่ไม่ครบฐานลงหรือไม่ — ถ้าไม่ปัดลงต้องบอกว่าเก็บเศษไว้รอบถัดไป */
  roundDown: boolean;
  /** เก็บเศษบาทที่ยังไม่ครบฐานไว้รวมกับรอบซื้อถัดไป */
  carryRemainder: boolean;

  /** อายุคะแนน — null = ไม่หมดอายุ (FR-PT-13) */
  pointExpiryMonths: number | null;
  /** แจ้งเตือนลูกค้าล่วงหน้ากี่วันก่อนคะแนนหมดอายุ */
  expiryNoticeDays: number;
  /** คะแนนหมดอายุแบบเข้าก่อนออกก่อน (FIFO) หรือหมดยกล็อตตามรอบปี */
  expiryMode: 'fifo' | 'annual-batch';

  resetCycle: ResetCycle;
  /** เดือนเริ่มรอบ (1–12) — ใช้เมื่อ resetCycle = 'fiscal' */
  fiscalStartMonth: number;
  resetPolicy: ResetPolicy;
  /** จำนวนวันผ่อนผัน — ใช้เมื่อ resetPolicy = 'grace-period' */
  graceDays: number;

  /** ส่วนลดแรกเข้า (SRS 3.4) — ค่าตั้งต้น 100 บาทเมื่อซื้อครบ 1,000 บาทครั้งแรก */
  welcomeDiscountBaht: number;
  welcomeMinSpend: number;
}

/** ค่าที่ใช้อยู่จริงตอนนี้ — ตรงกับ SRS 3.4 ทุกค่า */
export const CURRENT_POINT_RULE: PointRule = {
  spendPerPoint: 100,
  pointsEarned: 1,
  roundDown: true,
  carryRemainder: false,
  pointExpiryMonths: 24,
  expiryNoticeDays: 30,
  expiryMode: 'fifo',
  resetCycle: 'calendar',
  fiscalStartMonth: 10,
  resetPolicy: 'grace-period',
  graceDays: 90,
  welcomeDiscountBaht: 100,
  welcomeMinSpend: 1000,
};

/** ค่าที่ตั้งไว้ให้มีผลในอนาคต — null = ยังไม่มีการตั้งล่วงหน้า */
export interface ScheduledRuleChange {
  id: string;
  /** ISO date ที่จะมีผล */
  effectiveFrom: string;
  rule: PointRule;
  createdBy: string;
  createdAt: string;
  reason: string;
}

export const SCHEDULED_RULE_CHANGE: ScheduledRuleChange | null = null;

/** จำนวนผู้ได้รับผลกระทบเมื่อแก้กติกา — ตัวเลขนี้ต้องมาจาก API ตอนใช้จริง */
export const POINT_RULE_IMPACT = {
  activeMembers: 18_432,
  membersWithBalance: 12_907,
  totalOutstandingPoints: 4_186_540,
  pointsExpiringIn90Days: 214_880,
  ordersPerDay: 640,
  /** สมาชิกที่รอบรีเซ็ตจะขยับถ้าเปลี่ยน resetCycle */
  membersAffectedByCycleChange: 18_432,
};

/* ==========================================================================
   2. ระดับสมาชิกและสิทธิประโยชน์ (FR-ADM-23, SRS 3.4)
   ========================================================================== */

/**
 * id ของระดับสมาชิก — ใช้ **id ของระบบหลังบ้าน** (เป็นข้อความ) เมื่อต่อ API จริง
 *
 * ⚠️ เดิมล็อกไว้ 3 ค่า (silver / gold / platinum) แล้วจับคู่ด้วยชื่อระดับ
 *    ทำให้เพิ่ม ลบ หรือตั้งชื่ออื่นไม่ได้ และ division ที่ตั้งชื่อแบบอื่นใช้งานไม่ได้เลย
 *    ข้อมูลจำลองยังใช้ 'silver' / 'gold' / 'platinum' เป็น id ได้ตามเดิม
 */
export type TierId = string;

export interface TierBenefit {
  /**
   * id ของ "ความสัมพันธ์ระดับ↔รหัสสิทธิ์" ที่ backend เก็บ
   * รายการที่ผู้ใช้เพิ่งเพิ่มและยังไม่บันทึกจะเป็น id ชั่วคราวขึ้นต้นด้วย 'new-'
   */
  id: string;
  /**
   * รหัสสิทธิประโยชน์กลางที่เลือก
   *
   * ⚠️ backend เก็บรหัสเป็น enum ตายตัว (D03 D05 D10 D15 D20 D25 FS)
   *    และมีฟังก์ชันซิงก์ตารางแม่กับ enum — เพิ่มรหัสใหม่ = แก้โค้ด backend
   *    หน้าจอจึงให้ "เลือกจากรายการ" ไม่ใช่ "พิมพ์เอง"
   */
  codeId: string;
  /** ข้อความที่ลูกค้าเห็นในแอป — มาจากรหัสที่เลือก แก้ที่นี่ไม่ได้ */
  text: string;
  /** มีปุ่มให้กดรับสิทธิ์หรือไม่ (SRS 3.4: "สิทธิ์แต่ละรายการควรมีปุ่ม Claim") */
  claimable: boolean;
  /** เพดานการใช้สิทธิ์ต่อคนต่อปี — null = ไม่จำกัด */
  capPerYear: number | null;
  /** จำนวนครั้งที่ลูกค้ากดรับไปแล้วในรอบปีนี้ (รวมทุกคน) */
  claimedThisYear: number;
  /** ค่าตั้งต้นจากเอกสาร ยังไม่ยืนยัน */
  provisional: boolean;
}

export interface Tier {
  id: TierId;
  name: string;
  /** ยอดซื้อสะสมต่อปีขั้นต่ำ (บาท) */
  minSpend: number;
  /** ขั้นสูง — null = ไม่มีเพดาน (ระดับสูงสุด) */
  maxSpend: number | null;
  benefits: TierBenefit[];
  /** ภาพประกอบสิทธิประโยชน์ 16:9 ที่ขึ้นบนแอป */
  imageUrl: string | null;
  imageAlt: string;
  memberCount: number;
  provisional: boolean;
}

export const TIERS: Tier[] = [
  {
    id: 'silver',
    name: 'Silver',
    minSpend: 0,
    maxSpend: 5000,
    memberCount: 14_206,
    imageUrl: null,
    imageAlt: '',
    provisional: true,
    benefits: [
      { id: 's1', codeId: 'D03', text: 'ระบบสะสมแต้มแลกของรางวัล', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
      { id: 's2', codeId: 'D05', text: 'รับข่าวสารและโปรโมชัน (Newsletter สุขภาพ & โภชนาการ)', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
      { id: 's3', codeId: 'D10', text: 'สิทธิ์เข้าร่วมกิจกรรมสุขภาพออนไลน์', claimable: true, capPerYear: null, claimedThisYear: 1_842, provisional: true },
      { id: 's4', codeId: 'D15', text: 'โปรโมชันพิเศษเดือนเกิด', claimable: true, capPerYear: 1, claimedThisYear: 906, provisional: true },
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    minSpend: 5000,
    maxSpend: 20_000,
    memberCount: 3_388,
    imageUrl: null,
    imageAlt: '',
    provisional: true,
    benefits: [
      { id: 'g1', codeId: 'D03', text: 'สิทธิ์ทั้งหมดของ Silver', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
      { id: 'g2', codeId: 'D15', text: 'ส่วนลด 150 บาทในการซื้อครั้งถัดไป (ขั้นต่ำ 2,000 บาท)', claimable: true, capPerYear: 1, claimedThisYear: 1_204, provisional: true },
      { id: 'g3', codeId: 'D20', text: 'สิทธิ์ทดลองผลิตภัณฑ์ใหม่ก่อนใคร', claimable: true, capPerYear: 2, claimedThisYear: 388, provisional: true },
      { id: 'g4', codeId: 'FS', text: 'ฟรีค่าส่งเมื่อซื้อครบ 2,000 บาท', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
    ],
  },
  {
    id: 'platinum',
    name: 'Platinum',
    minSpend: 20_000,
    maxSpend: null,
    memberCount: 838,
    imageUrl: null,
    imageAlt: '',
    provisional: true,
    benefits: [
      { id: 'p1', codeId: 'D15', text: 'สิทธิ์ทั้งหมดของ Gold', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
      { id: 'p2', codeId: 'D20', text: 'ส่วนลด 300 บาทในการซื้อครั้งถัดไป (ขั้นต่ำ 3,000 บาท)', claimable: true, capPerYear: 1, claimedThisYear: 512, provisional: true },
      { id: 'p3', codeId: 'D25', text: 'ส่วนลดพิเศษ 10% จากราคาปกติทุกการซื้อ', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
      { id: 'p4', codeId: 'FS', text: 'ฟรีค่าส่งเมื่อซื้อครบ 500 บาท', claimable: false, capPerYear: null, claimedThisYear: 0, provisional: true },
    ],
  },
];

/**
 * การกระจายยอดซื้อสะสมรายปีของสมาชิก — ใช้คำนวณว่าถ้าขยับเกณฑ์ Tier แล้วใครย้ายระดับ
 * ถ้าไม่มีตัวเลขชุดนี้ คนตั้งเกณฑ์จะขยับเลขโดยไม่รู้ว่ากระทบกี่คน
 * ตอนใช้จริงต้องมาจาก API (สรุปยอดสะสมรายปีของสมาชิกทุกคน) ไม่ใช่ค่าคงที่
 */
export const SPEND_HISTOGRAM: Array<{ from: number; to: number; members: number }> = [
  { from: 0, to: 1_000, members: 6_842 },
  { from: 1_000, to: 2_500, members: 3_914 },
  { from: 2_500, to: 5_000, members: 3_450 },
  { from: 5_000, to: 7_500, members: 1_602 },
  { from: 7_500, to: 10_000, members: 908 },
  { from: 10_000, to: 15_000, members: 604 },
  { from: 15_000, to: 20_000, members: 274 },
  { from: 20_000, to: 30_000, members: 486 },
  { from: 30_000, to: 50_000, members: 258 },
  { from: 50_000, to: Number.POSITIVE_INFINITY, members: 94 },
];

/** จำนวนสมาชิกที่ยอดสะสมอยู่ในช่วง [min, max) — ประมาณจากฮิสโทแกรมแบบกระจายสม่ำเสมอในถัง */
export function membersInRange(min: number, max: number | null): number {
  const hi = max ?? Number.POSITIVE_INFINITY;
  let total = 0;
  for (const b of SPEND_HISTOGRAM) {
    const lo = Math.max(b.from, min);
    const up = Math.min(b.to, hi);
    if (up <= lo) continue;
    if (!Number.isFinite(b.to)) { total += b.members; continue; }
    total += Math.round(b.members * ((up - lo) / (b.to - b.from)));
  }
  return total;
}

/** ความยาวข้อความสิทธิประโยชน์ที่ยังไม่ตัดบรรทัดบนจอ 390px */
/**
 * รหัสสิทธิประโยชน์ที่ระบบมี — **ชุดเดียวกับ enum ฝั่ง backend**
 *
 * ⚠️ รายการนี้เป็นข้อมูลสำรองสำหรับดูหน้าจอโดยไม่มี backend เท่านั้น
 *    ตอนต่อของจริง ชั้น API จะส่ง benefitCatalog เข้ามาทาง prop แล้วใช้ตัวนั้นแทน
 *    ถ้า backend เพิ่มรหัสใหม่ รายการนี้จะล้าสมัย — แต่ไม่กระทบของจริง
 */
export interface BenefitCatalogItem {
  /** id ของรหัสในตารางแม่ (ตอนใช้ข้อมูลจำลองใช้ตัวรหัสเป็น id ไปเลย) */
  codeId: string;
  /** รหัสตาม enum เช่น D10 */
  code: string;
  /** ข้อความที่ลูกค้าเห็น */
  text: string;
}

export const BENEFIT_CATALOG: BenefitCatalogItem[] = [
  { codeId: 'D03', code: 'D03', text: 'ส่วนลด 3%' },
  { codeId: 'D05', code: 'D05', text: 'ส่วนลด 5%' },
  { codeId: 'D10', code: 'D10', text: 'ส่วนลด 10%' },
  { codeId: 'D15', code: 'D15', text: 'ส่วนลด 15%' },
  { codeId: 'D20', code: 'D20', text: 'ส่วนลด 20%' },
  { codeId: 'D25', code: 'D25', text: 'ส่วนลด 25%' },
  { codeId: 'FS', code: 'FS', text: 'ส่งฟรี' },
];

export const BENEFIT_TEXT_SAFE_LENGTH = 62;
export const BENEFIT_TEXT_MAX_LENGTH = 120;

/* ==========================================================================
   3. สิทธิพิเศษแบบกดรับ (SRS 3.4 — ปุ่ม Claim)
   ========================================================================== */

export type PrivilegeStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'ended';

export const PRIVILEGE_STATUS_LABEL: Record<PrivilegeStatus, string> = {
  draft: 'ฉบับร่าง',
  scheduled: 'ตั้งเวลาไว้',
  active: 'ใช้งานอยู่',
  paused: 'พักไว้',
  ended: 'สิ้นสุดแล้ว',
};

export const PRIVILEGE_STATUS_ICON: Record<PrivilegeStatus, string> = {
  draft: 'edit_note',
  scheduled: 'schedule_send',
  active: 'play_circle',
  paused: 'pause_circle',
  ended: 'stop_circle',
};

export interface Privilege {
  id: string;
  name: string;
  /** กลุ่มเป้าหมาย — ระดับสมาชิก หรือประเภทสมาชิก */
  audience: { tiers: TierId[]; note?: string };
  status: PrivilegeStatus;
  from: string;
  to: string;
  /** อายุของรหัสหลังลูกค้ากดรับ (นาที) */
  codeTtlMinutes: number;
  /** เพดานการกดรับต่อคนต่อรอบ — null = ไม่จำกัด */
  limitPerPerson: number | null;
  /** สถิติ — ตัวเลขที่สามสำคัญที่สุด */
  claimed: number;
  used: number;
  expiredUnused: number;
  /** ใช้ที่ไหน — หน้าร้าน / ในแอป / ทั้งสอง */
  channel: 'pos' | 'app' | 'both';
  provisional: boolean;
  /** คำอธิบายที่ลูกค้าเห็นในแอป */
  description?: string;
  /** ลูกค้าได้อะไรเมื่อใช้สิทธิ์ */
  benefitKind?: PrivilegeBenefitKind;
  /** มูลค่า — % หรือ บาท หรือ จำนวนชิ้น ตาม benefitKind */
  value?: number;
  /** เพดานส่วนลด (บาท) ใช้กับส่วนลดเป็นเปอร์เซ็นต์ */
  maxDiscount?: number | null;
  /** ยอดซื้อขั้นต่ำ (บาท) */
  minSpend?: number;
  /** false = จำกัดเฉพาะสินค้าที่ตั้งไว้จากระบบหลังบ้าน — หน้านี้ยังแก้รายการสินค้าไม่ได้ */
  allProducts?: boolean;
  /** รูปที่ลูกค้าเห็นในแอป — URL จากที่เก็บไฟล์ มีอายุจำกัด ห้ามเก็บถาวร */
  imageUrl?: string | null;
  imageAlt?: string;
  /** ไฟล์ที่เพิ่งเลือก ยังไม่ได้อัปโหลด */
  imageFile?: File;
  /** รอบที่นับเพดานต่อคนใหม่ — ไม่ระบุ = รายเดือน */
  recurrence?: PrivilegeRecurrence;
  /** จำนวนสิทธิ์ทั้งหมดของแคมเปญ — null = ไม่จำกัด */
  totalLimit?: number | null;
  /** ซ่อนปุ่มกดรับในแอปโดยไม่ต้องปิดทั้งสิทธิ์ */
  hasClaimButton?: boolean;
  /** รหัสอ้างอิงจากระบบหลังบ้าน เช่น BC1A2B3C4D */
  code?: string;
}

/** ประเภทสิทธิ์ที่ลูกค้าได้รับ */
export type PrivilegeBenefitKind = 'percent' | 'amount' | 'shipping' | 'product' | 'service';

export const PRIVILEGE_BENEFIT_LABEL: Record<PrivilegeBenefitKind, string> = {
  percent: 'ส่วนลดเป็นเปอร์เซ็นต์',
  amount: 'ส่วนลดเป็นบาท',
  shipping: 'ฟรีค่าจัดส่ง',
  product: 'แถมสินค้า',
  service: 'แถมบริการ',
};

/** รอบการนับเพดานต่อคน — ตรงกับที่ระบบหลังบ้านรองรับ */
export type PrivilegeRecurrence = 'weekly' | 'monthly' | 'yearly';

export const PRIVILEGE_RECURRENCE_LABEL: Record<PrivilegeRecurrence, string> = {
  weekly: 'ต่อสัปดาห์',
  monthly: 'ต่อเดือน',
  yearly: 'ต่อปี',
};

export const DEFAULT_CODE_TTL_MINUTES = 15;

/** เกินค่านี้ถือว่าอัตราหมดอายุโดยไม่ได้ใช้สูงผิดปกติ — กติกาเวลาน่าจะสั้นเกินไป */
export const EXPIRED_UNUSED_ALERT_PERCENT = 25;

export const PRIVILEGES: Privilege[] = [
  {
    id: 'pv-01', name: 'ส่วนลด 150 บาท สำหรับสมาชิก Gold', audience: { tiers: ['gold'] },
    benefitKind: 'amount', value: 150, minSpend: 1_500, allProducts: true,
    status: 'active', from: '2026-07-01', to: '2026-12-31',
    codeTtlMinutes: 15, limitPerPerson: 1,
    claimed: 1_204, used: 812, expiredUnused: 392, channel: 'both', provisional: true,
  },
  {
    id: 'pv-02', name: 'ส่วนลด 300 บาท สำหรับสมาชิก Platinum', audience: { tiers: ['platinum'] },
    benefitKind: 'amount', value: 300, minSpend: 3_000, allProducts: true,
    status: 'active', from: '2026-07-01', to: '2026-12-31',
    codeTtlMinutes: 15, limitPerPerson: 1,
    claimed: 512, used: 401, expiredUnused: 111, channel: 'both', provisional: true,
  },
  {
    id: 'pv-03', name: 'สิทธิ์ทดลองผลิตภัณฑ์ใหม่ — Omega 3 สูตรใหม่', audience: { tiers: ['gold', 'platinum'] },
    benefitKind: 'product', value: 1, minSpend: 0, allProducts: false,
    status: 'active', from: '2026-08-01', to: '2026-09-30',
    codeTtlMinutes: 15, limitPerPerson: 2,
    claimed: 388, used: 96, expiredUnused: 268, channel: 'app', provisional: true,
  },
  {
    id: 'pv-04', name: 'โปรโมชันเดือนเกิด — ส่วนลด 10%', audience: { tiers: ['silver', 'gold', 'platinum'], note: 'เฉพาะเดือนเกิดของสมาชิก' },
    benefitKind: 'percent', value: 10, maxDiscount: 500, minSpend: 0, allProducts: true,
    status: 'active', from: '2026-01-01', to: '2026-12-31',
    codeTtlMinutes: 60, limitPerPerson: 1,
    claimed: 906, used: 744, expiredUnused: 162, channel: 'both', provisional: true,
  },
  {
    id: 'pv-05', name: 'กิจกรรมสุขภาพออนไลน์ — คลาสโภชนาการ', audience: { tiers: ['silver', 'gold', 'platinum'] },
    benefitKind: 'service', value: 1, minSpend: 0, allProducts: false,
    status: 'active', from: '2026-08-15', to: '2026-08-31',
    codeTtlMinutes: 1_440, limitPerPerson: 1,
    claimed: 1_842, used: 1_615, expiredUnused: 227, channel: 'app', provisional: true,
  },
  {
    id: 'pv-06', name: 'ฟรีค่าส่ง สัปดาห์เปิดตัว Marketplace', audience: { tiers: ['gold', 'platinum'] },
    benefitKind: 'shipping', value: 0, minSpend: 0, allProducts: true,
    status: 'scheduled', from: '2026-09-01', to: '2026-09-07',
    codeTtlMinutes: 15, limitPerPerson: 3,
    claimed: 0, used: 0, expiredUnused: 0, channel: 'app', provisional: true,
  },
  {
    id: 'pv-07', name: 'ส่วนลดแรกเข้า 100 บาท (ซื้อครบ 1,000 บาท)', audience: { tiers: ['silver'], note: 'สมาชิกใหม่ที่ยังไม่เคยสั่งซื้อ' },
    benefitKind: 'amount', value: 100, minSpend: 1_000, allProducts: true,
    status: 'active', from: '2026-01-01', to: '2026-12-31',
    codeTtlMinutes: 4_320, limitPerPerson: 1,
    claimed: 2_410, used: 2_188, expiredUnused: 222, channel: 'both', provisional: false,
  },
  {
    id: 'pv-08', name: 'ตรวจสุขภาพพื้นฐาน ราคาพิเศษ (คลินิกพันธมิตร)', audience: { tiers: ['platinum'] },
    benefitKind: 'service', value: 1, minSpend: 0, allProducts: false,
    status: 'paused', from: '2026-06-01', to: '2026-08-31',
    codeTtlMinutes: 10_080, limitPerPerson: 1,
    claimed: 148, used: 61, expiredUnused: 12, channel: 'pos', provisional: true,
  },
  {
    id: 'pv-09', name: 'ชุดตัวอย่างวิตามิน สำหรับสมาชิกใหม่', audience: { tiers: ['silver'] },
    benefitKind: 'product', value: 1, minSpend: 0, allProducts: false,
    status: 'ended', from: '2026-03-01', to: '2026-04-30',
    codeTtlMinutes: 15, limitPerPerson: 1,
    claimed: 1_180, used: 402, expiredUnused: 778, channel: 'app', provisional: true,
  },
  {
    id: 'pv-10', name: 'สิทธิ์ซื้อก่อนใคร — Collagen Plus', audience: { tiers: ['gold', 'platinum'] },
    status: 'draft', from: '2026-10-01', to: '2026-10-31',
    codeTtlMinutes: 15, limitPerPerson: 1,
    claimed: 0, used: 0, expiredUnused: 0, channel: 'app', provisional: true,
  },
];

/* ==========================================================================
   4. ของรางวัล (FR-ADM-22, FR-RD-01..05)
   ========================================================================== */

/** สามประเภทตาม FR-RD-02 */
export type RewardType = 'product' | 'coupon' | 'shipping';

export const REWARD_TYPE_LABEL: Record<RewardType, string> = {
  product: 'สินค้าจริง (ต้องจัดส่ง)',
  coupon: 'คูปองส่วนลด / E-Voucher',
  shipping: 'สิทธิ์ฟรีค่าจัดส่ง',
};

export const REWARD_TYPE_ICON: Record<RewardType, string> = {
  product: 'inventory_2',
  coupon: 'confirmation_number',
  shipping: 'local_shipping',
};

/** สิ่งที่แต่ละประเภทต้องกรอกต่างกัน — หน้าจอซ่อนช่องที่ประเภทนั้นไม่ใช้ */
export const REWARD_TYPE_FIELDS: Record<RewardType, string[]> = {
  product: ['stock', 'shipping', 'sku'],
  coupon: ['couponValidDays', 'couponValue', 'minSpend'],
  shipping: ['couponValidDays', 'minSpend'],
};

/**
 * ส่วนของหน้าแคตตาล็อกที่ backend อาจยังไม่รองรับ — ชั้น integration ส่งมาบอกให้ซ่อน
 *   stock           สต๊อกคงเหลือ · การเตือน · ตัวกรองและตัวเลขสรุปที่เกี่ยวกับสต๊อก
 *   limitPerPerson  จำกัดกี่ครั้งต่อคน
 *   redeemedTotal   จำนวนที่แลกไปแล้วและคะแนนที่ถูกใช้
 *   imageUpload     การอัปโหลดรูป (รูปที่มีอยู่แล้วยังแสดงได้)
 */
export type RewardFeature = 'stock' | 'limitPerPerson' | 'redeemedTotal' | 'imageUpload';

/** ส่วนลดของคูปองที่เป็นของรางวัล — ชุดเดียวกับชนิดในหน้าคูปอง (ไม่รวมฟรีค่าส่ง ซึ่งเป็นประเภทของรางวัลแยก) */
export interface RewardVoucher {
  kind: 'amount' | 'percent';
  /** บาท หรือ % ตาม kind */
  value: number | null;
  /** เพดานส่วนลด (บาท) — ต้องมีเมื่อเป็น % · แบบบาทใช้ค่าเดียวกับ value */
  maxDiscount: number | null;
}

export interface Reward {
  id: string;
  name: string;
  type: RewardType;
  /** คะแนนที่ใช้แลก */
  points: number;
  /** สต๊อกคงเหลือ — null = ไม่จำกัด (คูปองและสิทธิ์ฟรีค่าส่งมักไม่จำกัด) */
  stock: number | null;
  /** แจ้งเตือนเมื่อสต๊อกเหลือถึงค่านี้ */
  stockAlertAt: number | null;
  from: string;
  to: string;
  /** จำกัดกี่ครั้งต่อคน — null = ไม่จำกัด */
  limitPerPerson: number | null;
  /** อายุคูปองหลังแลก (วัน) — ใช้กับประเภท coupon และ shipping */
  couponValidDays: number | null;
  /** มูลค่าคูปองที่ออกให้เมื่อแลก — ใช้กับประเภท coupon เท่านั้น */
  voucher?: RewardVoucher;
  /** ยอดซื้อขั้นต่ำที่ใช้คูปองได้ (บาท) — ใช้กับประเภท coupon และ shipping */
  minSpend?: number;
  /** จำนวนคูปองที่ได้ต่อการแลกหนึ่งครั้ง — ไม่ใช่สต๊อก · ไม่ระบุ = 1 */
  quantity?: number;
  /** แพ็กเกจที่ลูกค้าได้เมื่อแลก — ใช้กับประเภทสินค้า */
  productId?: number | null;
  /** เงื่อนไขการใช้ที่ลูกค้าจะเห็น */
  conditions: string;
  imageUrl: string | null;
  imageAlt: string;
  /** ไฟล์ที่เพิ่งเลือก ยังไม่ได้อัปโหลด — ชั้นข้อมูลอัปโหลดให้หลังบันทึกสำเร็จ */
  imageFile?: File;
  /**
   * id ของแพ็กเกจที่ภาพนี้คัดลอกมา — มีค่าเฉพาะระหว่างแก้ ยังไม่ได้บันทึก
   * ชั้นข้อมูลดึงภาพจาก `imageUrl` ไปส่งให้ backend เก็บเป็นภาพของของรางวัลเอง
   */
  imageFromPackage?: number;
  enabled: boolean;
  redeemedTotal: number;
  provisional: boolean;
}

export const REWARDS: Reward[] = [
  {
    id: 'rw-01', name: 'Innobic Multivitamin 30 เม็ด', type: 'product', points: 1_200,
    stock: 42, stockAlertAt: 50, from: '2026-06-01', to: '2026-12-31', limitPerPerson: 2,
    couponValidDays: null, conditions: 'จัดส่งภายใน 7 วันทำการ เฉพาะที่อยู่ในประเทศไทย',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 318, provisional: true,
  },
  {
    id: 'rw-02', name: 'คูปองส่วนลด 100 บาท', type: 'coupon', points: 500,
    stock: null, stockAlertAt: null, from: '2026-01-01', to: '2026-12-31', limitPerPerson: 5,
    couponValidDays: 30, voucher: { kind: 'amount', value: 100, maxDiscount: 100 }, minSpend: 800, quantity: 1,
    conditions: 'ใช้ได้เมื่อซื้อครบ 800 บาท ใช้ร่วมกับส่วนลดอื่นไม่ได้',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 2_046, provisional: true,
  },
  {
    id: 'rw-03', name: 'คูปองส่วนลด 300 บาท', type: 'coupon', points: 1_400,
    stock: 200, stockAlertAt: 30, from: '2026-01-01', to: '2026-12-31', limitPerPerson: 2,
    couponValidDays: 30, voucher: { kind: 'amount', value: 300, maxDiscount: 300 }, minSpend: 2_000, quantity: 1,
    conditions: 'ใช้ได้เมื่อซื้อครบ 2,000 บาท ใช้ร่วมกับส่วนลดอื่นไม่ได้',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 604, provisional: true,
  },
  {
    id: 'rw-04', name: 'สิทธิ์ฟรีค่าจัดส่ง 1 ครั้ง', type: 'shipping', points: 200,
    stock: null, stockAlertAt: null, from: '2026-01-01', to: '2026-12-31', limitPerPerson: 10,
    couponValidDays: 60, minSpend: 0, quantity: 1,
    conditions: 'ใช้ได้กับการสั่งซื้อในแอปเท่านั้น ไม่จำกัดยอดขั้นต่ำ',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 4_882, provisional: true,
  },
  {
    id: 'rw-05', name: 'Innobic Omega 3 60 แคปซูล', type: 'product', points: 2_400,
    stock: 8, stockAlertAt: 20, from: '2026-06-01', to: '2026-12-31', limitPerPerson: 1,
    couponValidDays: null, conditions: 'จัดส่งภายใน 7 วันทำการ เฉพาะที่อยู่ในประเทศไทย',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 96, provisional: true,
  },
  {
    id: 'rw-06', name: 'กระเป๋าผ้า Innobic Limited', type: 'product', points: 800,
    stock: 0, stockAlertAt: 25, from: '2026-05-01', to: '2026-09-30', limitPerPerson: 1,
    couponValidDays: null, conditions: 'ของมีจำนวนจำกัด จัดส่งภายใน 14 วันทำการ',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 500, provisional: true,
  },
  {
    id: 'rw-07', name: 'Innobic Probiotic 20 ซอง', type: 'product', points: 1_800,
    stock: 118, stockAlertAt: 30, from: '2026-08-01', to: '2027-01-31', limitPerPerson: 2,
    couponValidDays: null, conditions: 'จัดส่งภายใน 7 วันทำการ เฉพาะที่อยู่ในประเทศไทย',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 41, provisional: true,
  },
  {
    id: 'rw-08', name: 'คูปองส่วนลด 50 บาท (สมาชิกใหม่)', type: 'coupon', points: 250,
    stock: null, stockAlertAt: null, from: '2026-01-01', to: '2026-12-31', limitPerPerson: 1,
    couponValidDays: 14, voucher: { kind: 'amount', value: 50, maxDiscount: 50 }, minSpend: 500, quantity: 1,
    conditions: 'ใช้ได้เมื่อซื้อครบ 500 บาท เฉพาะการสั่งซื้อครั้งแรก',
    imageUrl: null, imageAlt: '', enabled: false, redeemedTotal: 1_308, provisional: true,
  },
  {
    id: 'rw-09', name: 'บัตรกำนัลตรวจสุขภาพ (คลินิกพันธมิตร)', type: 'coupon', points: 6_000,
    stock: 25, stockAlertAt: 10, from: '2026-07-01', to: '2026-12-31', limitPerPerson: 1,
    couponValidDays: 90, voucher: { kind: 'percent', value: 100, maxDiscount: 2_500 }, minSpend: 0, quantity: 1,
    conditions: 'นัดหมายล่วงหน้า 7 วัน ใช้ได้ที่คลินิกพันธมิตรที่ร่วมรายการ',
    imageUrl: null, imageAlt: '', enabled: true, redeemedTotal: 12, provisional: true,
  },
];

/* ==========================================================================
   5. คูปองและโค้ดส่วนลด (FR-ADM-20, 21)
   ========================================================================== */

export type CouponKind = 'amount' | 'percent' | 'freeship';

export const COUPON_KIND_LABEL: Record<CouponKind, string> = {
  amount: 'ลดเป็นจำนวนเงิน',
  percent: 'ลดเป็นเปอร์เซ็นต์',
  freeship: 'ฟรีค่าจัดส่ง',
};

/** เงื่อนไขการใช้ร่วมกับส่วนลดอื่น (FR-ADM-20) — คำถามที่ยังค้างกับทีม backend ยังไม่ตอบ ค่าตั้งต้นจึงเป็น "ห้ามใช้ร่วม" */
export type StackRule = 'exclusive' | 'with-tier' | 'with-points' | 'all';

export const STACK_RULE_LABEL: Record<StackRule, string> = {
  exclusive: 'ใช้ร่วมกับส่วนลดอื่นไม่ได้',
  'with-tier': 'ใช้ร่วมกับส่วนลดตามระดับสมาชิกได้',
  'with-points': 'ใช้ร่วมกับการแลกคะแนนได้',
  all: 'ใช้ร่วมกับส่วนลดทุกประเภทได้',
};

export type CouponStatus = 'draft' | 'active' | 'paused' | 'expired' | 'exhausted';

export const COUPON_STATUS_LABEL: Record<CouponStatus, string> = {
  draft: 'ฉบับร่าง',
  active: 'ใช้งานอยู่',
  paused: 'พักไว้',
  expired: 'หมดอายุ',
  exhausted: 'สิทธิ์หมด',
};

export interface Coupon {
  id: string;
  /** โค้ดที่ลูกค้ากรอก — ชุด (batch) จะเป็น prefix + running */
  code: string;
  name: string;
  kind: CouponKind;
  /** มูลค่า: บาท (amount) หรือ % (percent) — freeship ไม่ใช้ */
  value: number | null;
  /** เพดานส่วนลดเมื่อคิดเป็น % — กันกรณีตะกร้าใหญ่ */
  maxDiscount: number | null;
  minSpend: number;
  from: string;
  expiresAt: string;
  /** จำนวนสิทธิ์รวม — null = ไม่จำกัด */
  totalQuota: number | null;
  usedCount: number;
  /** จำนวนสิทธิ์ต่อคน */
  perPerson: number;
  stackRule: StackRule;
  status: CouponStatus;
  /** ชุดโค้ด — null = โค้ดเดี่ยว */
  /**
   * ชุดโค้ด — null = โค้ดเดี่ยว
   * `prefix` คือโค้ดจริงที่เก็บ ส่วนสุ่มต่อท้ายระบบสร้างให้เอง · `codeLength` ใช้ตอนสร้างเท่านั้น
   */
  batch: { prefix: string; size: number; issued: number; codeLength?: number } | null;
  createdBy: string;
  provisional: boolean;
  /** รูปที่ลูกค้าเห็นในแอป — URL จากที่เก็บไฟล์ มีอายุจำกัด ห้ามเก็บถาวร */
  imageUrl?: string | null;
  imageAlt?: string;
  /** ไฟล์ที่เพิ่งเลือก ยังไม่ได้อัปโหลด */
  imageFile?: File;
}

export const COUPONS: Coupon[] = [
  {
    id: 'cp-01', code: 'WELCOME100', name: 'ส่วนลดแรกเข้า 100 บาท', kind: 'amount', value: 100,
    maxDiscount: null, minSpend: 1_000, from: '2026-01-01', expiresAt: '2026-12-31',
    totalQuota: null, usedCount: 2_188, perPerson: 1, stackRule: 'exclusive', status: 'active',
    batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: false,
  },
  {
    id: 'cp-02', code: 'GOLD150', name: 'ส่วนลด 150 บาท สมาชิก Gold', kind: 'amount', value: 150,
    maxDiscount: null, minSpend: 2_000, from: '2026-07-01', expiresAt: '2026-12-31',
    totalQuota: 4_000, usedCount: 812, perPerson: 1, stackRule: 'with-tier', status: 'active',
    batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: true,
  },
  {
    id: 'cp-03', code: 'PLAT300', name: 'ส่วนลด 300 บาท สมาชิก Platinum', kind: 'amount', value: 300,
    maxDiscount: null, minSpend: 3_000, from: '2026-07-01', expiresAt: '2026-12-31',
    totalQuota: 1_000, usedCount: 401, perPerson: 1, stackRule: 'with-tier', status: 'active',
    batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: true,
  },
  {
    id: 'cp-04', code: 'BDAY10', name: 'โปรโมชันเดือนเกิด ลด 10%', kind: 'percent', value: 10,
    maxDiscount: 500, minSpend: 500, from: '2026-01-01', expiresAt: '2026-12-31',
    totalQuota: null, usedCount: 744, perPerson: 1, stackRule: 'exclusive', status: 'active',
    batch: null, createdBy: 'ปิยะพงษ์ ศรีสุข', provisional: true,
  },
  {
    id: 'cp-05', code: 'FREESHIP', name: 'ฟรีค่าจัดส่ง (แลกด้วยคะแนน)', kind: 'freeship', value: null,
    maxDiscount: null, minSpend: 0, from: '2026-01-01', expiresAt: '2026-12-31',
    totalQuota: null, usedCount: 4_882, perPerson: 10, stackRule: 'with-points', status: 'active',
    batch: null, createdBy: 'ปิยะพงษ์ ศรีสุข', provisional: true,
  },
  {
    id: 'cp-06', code: 'PTTFAIR26-••••', name: 'งาน PTT Health Fair 2026', kind: 'amount', value: 200,
    maxDiscount: null, minSpend: 1_500, from: '2026-09-10', expiresAt: '2026-09-20',
    totalQuota: 3_000, usedCount: 0, perPerson: 1, stackRule: 'exclusive', status: 'draft',
    batch: { prefix: 'PTTFAIR26', size: 3_000, issued: 0 }, createdBy: 'ปิยะพงษ์ ศรีสุข', provisional: true,
  },
  {
    id: 'cp-07', code: 'SUMMER15', name: 'โปรโมชันกลางปี ลด 15%', kind: 'percent', value: 15,
    maxDiscount: 800, minSpend: 1_200, from: '2026-05-01', expiresAt: '2026-06-30',
    totalQuota: 5_000, usedCount: 3_411, perPerson: 2, stackRule: 'exclusive', status: 'expired',
    batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: true,
  },
  {
    id: 'cp-08', code: 'LINEOA50-••••', name: 'ชุดโค้ด LINE OA 50 บาท', kind: 'amount', value: 50,
    maxDiscount: null, minSpend: 400, from: '2026-08-01', expiresAt: '2026-08-31',
    totalQuota: 2_000, usedCount: 2_000, perPerson: 1, stackRule: 'exclusive', status: 'exhausted',
    batch: { prefix: 'LINEOA50', size: 2_000, issued: 2_000 }, createdBy: 'ปิยะพงษ์ ศรีสุข', provisional: true,
  },
  {
    id: 'cp-09', code: 'NEWAPP80', name: 'ดาวน์โหลดแอปครั้งแรก ลด 80 บาท', kind: 'amount', value: 80,
    maxDiscount: null, minSpend: 600, from: '2026-08-15', expiresAt: '2026-10-31',
    totalQuota: 10_000, usedCount: 128, perPerson: 1, stackRule: 'exclusive', status: 'paused',
    batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: true,
  },
];

/** ค่าตั้งต้นของการสร้างโค้ดแบบชุด (FR-ADM-21) */
export const BATCH_DEFAULTS = {
  prefix: '',
  size: 500,
  /** ความยาวส่วนสุ่มต่อท้าย prefix */
  randomLength: 6,
  /** ชุดตัวอักษรที่ใช้ — ตัด 0/O/1/I/L ออกเพราะคนอ่านผิดเวลาพิมพ์จากกระดาษ */
  alphabet: 'ABCDEFGHJKMNPQRSTUVWXYZ23456789',
  maxSize: 50_000,
};

/* ==========================================================================
   6. แบนเนอร์หน้าแรก (FR-HOME-06)
   ========================================================================== */

export type BannerStatus = 'draft' | 'scheduled' | 'live' | 'ended';

export const BANNER_STATUS_LABEL: Record<BannerStatus, string> = {
  draft: 'ฉบับร่าง',
  scheduled: 'ตั้งเวลาไว้',
  live: 'แสดงอยู่',
  ended: 'สิ้นสุดแล้ว',
};

export interface Banner {
  id: string;
  /** ชื่ออ้างอิงภายใน — ใช้ในรายงานและประวัติการแก้ไข ลูกค้าไม่เห็น */
  name: string;
  /** หัวข้อที่ลูกค้าเห็นบนการ์ด */
  title: string;
  /** บรรทัดรองใต้หัวข้อ เช่น "ถึง 31 ต.ค. 2569" */
  caption: string;
  /** ป้ายมุมซ้ายบนของการ์ด เช่น "เฉพาะสมาชิก Gold" */
  tag: string;
  /** ไอคอนสำรองเมื่อไม่มีภาพ — ต้องเลือกจาก BANNER_ICONS เท่านั้น */
  icon: string;
  /** ลำดับการแสดง — 1 คือใบแรกที่ลูกค้าเห็น */
  order: number;
  imageUrl: string | null;
  imageAlt: string;
  /** ไฟล์ที่เพิ่งเลือก ยังไม่ได้อัปโหลด — ชั้นข้อมูลส่งไปกับคำขอบันทึก */
  imageFile?: File;
  /** วันเริ่มแสดง YYYY-MM-DD · '' = ยังไม่กำหนด */
  from: string;
  /** วันสุดท้ายที่แสดง YYYY-MM-DD · null = ไม่มีกำหนดสิ้นสุด */
  to: string | null;
  /**
   * เผยแพร่หรือยัง — สถานะที่เหลือคำนวณจากวันที่ ดู bannerStatus()
   * ⚠️ ไม่เก็บสถานะเป็นค่าที่เลือกเอง ไม่งั้นจะมีใบที่บอก "แสดงอยู่" ทั้งที่เลยวันสิ้นสุดแล้ว
   */
  isPublished: boolean;
  /** กลุ่มผู้รับ — ว่าง = ทุกระดับ · segment เป็นข้อความของงานออกแบบเดิม (โหมดสาธิตเท่านั้น) */
  audience: { tiers: TierId[]; segment?: string };
  /** ปลายทางเมื่อกด — path ภายในแอป · '' = การ์ดกดไม่ได้ */
  linkTo: string;
  impressions: number;
  taps: number;
}

/** วันนี้ตามเวลาไทย YYYY-MM-DD — ใช้เทียบกับช่วงวันที่ของแบนเนอร์ */
export const todayInBangkok = (): string =>
  new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);

/**
 * สถานะของแบนเนอร์ — กติกาเดียวกับที่ backend คำนวณ
 * ใช้ตอนแก้ในหน้าจอ เพื่อให้ป้ายสถานะเปลี่ยนตามวันที่ทันทีโดยไม่ต้องรอบันทึก
 */
export function bannerStatus(b: Pick<Banner, 'isPublished' | 'from' | 'to'>, today = todayInBangkok()): BannerStatus {
  if (!b.isPublished) return 'draft';
  if (!b.from || b.from > today) return 'scheduled';
  if (b.to && b.to < today) return 'ended';
  return 'live';
}

/**
 * ไอคอนสำรองที่เลือกได้ — แสดงบนการ์ดในแอปลูกค้าเมื่อแบนเนอร์ไม่มีภาพ
 *
 * ⚠️ ต้องมีทั้งในฟอนต์ไอคอนของแอปลูกค้า (app/scripts/icons.txt) และของหลังบ้าน (scripts/icons.txt)
 *    ไอคอนที่ไม่อยู่ในฟอนต์จะขึ้นเป็นชื่อดิบ เช่นคำว่า CHECK — จึงไม่ให้พิมพ์ชื่อเอง
 */
export const BANNER_ICONS: Array<{ name: string; label: string }> = [
  { name: 'redeem', label: 'ของรางวัล' },
  { name: 'confirmation_number', label: 'คูปอง' },
  { name: 'local_shipping', label: 'ส่งฟรี' },
  { name: 'workspace_premium', label: 'สมาชิกระดับ' },
  { name: 'payments', label: 'ส่วนลด / คะแนน' },
  { name: 'shopping_cart', label: 'สินค้า' },
  { name: 'inventory_2', label: 'แพ็กเกจ' },
  { name: 'badge', label: 'พนักงาน' },
  { name: 'event', label: 'กิจกรรม' },
  { name: 'notifications', label: 'ข่าวสาร' },
  { name: 'verified', label: 'รับรอง' },
  { name: 'receipt_long', label: 'คำสั่งซื้อ' },
];

const mockBanner = (b: Partial<Banner> & Pick<Banner, 'id' | 'title' | 'order' | 'from' | 'to' | 'isPublished' | 'linkTo'>): Banner => ({
  name: b.title, caption: '', tag: '', icon: 'redeem', imageUrl: null, imageAlt: '',
  audience: { tiers: [] }, impressions: 0, taps: 0, ...b,
});

export const BANNERS: Banner[] = [
  mockBanner({
    id: 'bn-01', title: 'แลกคะแนนรับคูปองส่วนลด 300 บาท', order: 1, tag: 'แคมเปญใหม่', caption: 'ถึง 31 ธ.ค. 2569',
    icon: 'confirmation_number', from: '2026-08-01', to: '2026-12-31', isPublished: true,
    linkTo: '/rewards', impressions: 84_210, taps: 6_118,
  }),
  mockBanner({
    id: 'bn-02', title: 'สิทธิพิเศษสมาชิก Gold — ส่วนลด 150 บาท', order: 2, tag: 'เฉพาะสมาชิก Gold',
    icon: 'workspace_premium', from: '2026-07-01', to: '2026-12-31', isPublished: true,
    audience: { tiers: ['gold'] }, linkTo: '/privileges/pv-01', impressions: 21_440, taps: 2_902,
  }),
  mockBanner({
    id: 'bn-03', title: 'Omega 3 สูตรใหม่ ทดลองก่อนใคร', order: 3, from: '2026-08-01', to: '2026-12-31', isPublished: true,
    audience: { tiers: ['gold', 'platinum'] }, linkTo: '/privileges/pv-03', impressions: 18_066, taps: 1_204,
  }),
  mockBanner({
    id: 'bn-04', title: 'สมัครสมาชิกใหม่ รับส่วนลด 100 บาท', order: 4, from: '2026-01-01', to: '2026-12-31', isPublished: true,
    audience: { tiers: ['silver'], segment: 'ยังไม่เคยสั่งซื้อ' }, linkTo: '/promo/welcome', impressions: 46_882, taps: 5_401,
  }),
  mockBanner({
    id: 'bn-05', title: 'PTT Health Fair 2026', order: 5, icon: 'event', from: '2026-11-10', to: '2026-11-20', isPublished: true,
    audience: { tiers: [], segment: 'กลุ่ม ปตท.' }, linkTo: '/campaign/ptt-fair',
  }),
  mockBanner({
    id: 'bn-06', title: 'โปรโมชันกลางปี ลด 15%', order: 6, from: '2026-05-01', to: '2026-06-30', isPublished: true,
    linkTo: '/promo/summer', impressions: 132_004, taps: 14_882,
  }),
  mockBanner({
    id: 'bn-07', title: 'Collagen Plus เปิดจอง', order: 7, from: '2026-11-01', to: '2026-11-30', isPublished: false,
    audience: { tiers: ['gold', 'platinum'] }, linkTo: '/products/collagen-plus',
  }),
];

/** จำนวนแบนเนอร์ที่แสดงพร้อมกันได้บนหน้าแรกก่อนที่ลูกค้าจะเลื่อนผ่านทั้งหมด */
export const BANNER_VISIBLE_LIMIT = 5;

/* ==========================================================================
   7. ประวัติการแก้ไข (FR-ADM-37)
   ========================================================================== */

export interface LoyaltyAudit {
  id: string;
  at: string;
  actorName: string;
  actorRole: string;
  action: string;
  icon?: string;
  reason?: string;
  changes?: Array<{ field: string; before: string | null; after: string | null }>;
  automated?: boolean;
}

export const POINT_RULE_AUDIT: LoyaltyAudit[] = [
  {
    id: 'au-01', at: '2026-08-20T14:22:00+07:00', actorName: 'สุดารัตน์ ทองดี', actorRole: 'Admin — Marketing',
    action: 'แก้อายุคะแนน', icon: 'timer',
    reason: 'ที่ประชุม 18 ส.ค. ให้ทดลองอายุ 24 เดือนก่อน แล้วประเมินอีกครั้งสิ้นปี',
    changes: [{ field: 'อายุคะแนน', before: 'ไม่หมดอายุ', after: '24 เดือน' }],
  },
  {
    id: 'au-02', at: '2026-08-12T09:05:00+07:00', actorName: 'ปิยะพงษ์ ศรีสุข', actorRole: 'Admin — Marketing',
    action: 'แก้ระยะแจ้งเตือนก่อนคะแนนหมดอายุ', icon: 'notifications',
    reason: 'ลูกค้าบอกว่า 7 วันสั้นเกินไป ไม่ทันวางแผนใช้คะแนน',
    changes: [{ field: 'แจ้งเตือนล่วงหน้า', before: '7 วัน', after: '30 วัน' }],
  },
  {
    id: 'au-03', at: '2026-07-01T10:40:00+07:00', actorName: 'ระบบ', actorRole: 'ตั้งค่าเริ่มต้น',
    action: 'ตั้งกติกาคะแนนครั้งแรกจากเอกสาร SRS ข้อ 3.4', icon: 'settings', automated: true,
    changes: [
      { field: 'อัตราแลกเปลี่ยน', before: null, after: '100 บาท = 1 คะแนน' },
      { field: 'รอบรีเซ็ตยอดสะสม', before: null, after: 'ปีปฏิทิน (1 มกราคม)' },
      { field: 'ส่วนลดแรกเข้า', before: null, after: '100 บาท เมื่อซื้อครบ 1,000 บาท' },
    ],
  },
];

/* ------------------------------------------------------------ ตัวช่วยคำนวณ */

/** คะแนนที่ได้จากยอดซื้อหนึ่งครั้ง ตามกติกาที่ให้มา */
export function pointsFor(spend: number, rule: PointRule): { points: number; remainder: number } {
  if (rule.spendPerPoint <= 0) return { points: 0, remainder: spend };
  const units = spend / rule.spendPerPoint;
  const whole = Math.floor(units);
  const points = rule.roundDown ? whole * rule.pointsEarned : Math.round(units * rule.pointsEarned);
  const remainder = spend - whole * rule.spendPerPoint;
  return { points, remainder };
}

/** ยอดซื้อที่ต้องใช้เพื่อได้คะแนนตามจำนวนที่ระบุ */
export function spendFor(points: number, rule: PointRule): number {
  if (rule.pointsEarned <= 0) return 0;
  return Math.ceil((points / rule.pointsEarned) * rule.spendPerPoint);
}

export const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

/** นาที → ข้อความที่อ่านง่าย (15 นาที · 1 ชั่วโมง · 3 วัน) */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes.toLocaleString('th-TH')} นาที`;
  if (minutes < 1_440) {
    const h = minutes / 60;
    return `${(Number.isInteger(h) ? h : h.toFixed(1)).toLocaleString('th-TH')} ชั่วโมง`;
  }
  const d = minutes / 1_440;
  return `${(Number.isInteger(d) ? d : d.toFixed(1)).toLocaleString('th-TH')} วัน`;
}
